import type { Ref } from 'react';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { Button, intl, LoadMask, Tool } from 'ehs-react';
import { useViewStateStore } from '../../stores/viewState.store';
import type { at } from '../../generated/Beans';
import { useRegionInstitutionStore } from '../../stores/regionInstitution.store';
import { usePortletStore } from '../../App';
import { useListA11y } from '../../utils/utils';
type Institution = at.sense.portlets.saf.virtualvisitpatient.model.Institution;

export function SelectRegionAndInstitution(): JSX.Element {
  const [
    selectInstitution,
    appointmentMode,
    reconnectSessionMode,
    setAppointmentMode,
    setReconnectSessionMode,
    loggedInPatient,
    selectedInstitution
  ] = useViewStateStore(state => [
    state.selectInstitution,
    state.viewStateData?.appointmentMode,
    state.viewStateData?.reconnectSessionMode,
    state.setAppointmentMode,
    state.setReconnectSessionMode,
    state.viewStateData?.loggedInPatient,
    state.viewStateData?.selectedInstitution
  ]);

  const [
    regions,
    institutions,
    hospitals,
    loadRegions,
    loadInstitutions,
    loadHospitals,
    institutionsLoading
  ] = useRegionInstitutionStore(state => [
    state.regions,
    state.institutions,
    state.hospitals,
    state.loadRegions,
    state.loadInstitutions,
    state.loadHospitals,
    state.loading
  ]);

  const [region, setRegion] = useState<Institution | null>(null);
  const [hospital, setHospital] = useState<Institution | null>(null);

  useEffect(() => {
    loadRegions();
  }, [loadRegions]);

  const [hpdHierarchy] = usePortletStore(store => [
    store.configuration.hpdHierarchy
  ]);

  const selRegion = useCallback(
    (newVal: Institution | null) => {
      setRegion(newVal);
      if (newVal !== null && hpdHierarchy === 'TWO_LAYER_HIERARCHY') {
        loadInstitutions(newVal.id);
      } else if (newVal !== null && hpdHierarchy === 'THREE_LAYER_HIERARCHY') {
        loadHospitals(newVal.id);
      } else if (!newVal) {
        setHospital(null);
      }
    },
    [setRegion, loadHospitals, loadInstitutions, hpdHierarchy]
  );
  function selHospital(newVal: Institution | null) {
    setHospital(newVal);
    if (newVal !== null) {
      loadInstitutions(newVal.id);
    }
  }

  function selInst(newVal: Institution | null) {
    if (newVal !== null) {
      selectInstitution(newVal);
    }
  }

  const showHospitals = region && hpdHierarchy === 'THREE_LAYER_HIERARCHY';
  const showInstitutions =
    (region && hospital && hpdHierarchy === 'THREE_LAYER_HIERARCHY') ||
    (region && hpdHierarchy === 'TWO_LAYER_HIERARCHY');

  let selRegionStr = intl('virtualvisitpatientportlet.region.dropdown.label');

  if (appointmentMode) {
    selRegionStr = intl(
      'virtualvisitpatientportlet.region.dropdown.appointment.label'
    );
  } else if (reconnectSessionMode) {
    selRegionStr = intl(
      'virtualvisitpatientportlet.region.dropdown.reconnectsession.label'
    );
  }
  let noInstitutionFoundStr = '';
  if (hpdHierarchy === 'THREE_LAYER_HIERARCHY') {
    noInstitutionFoundStr = intl(
      'virtualvisitpatientportlet.region.no.institution.three.layer.label'
    );
  } else if (hpdHierarchy === 'TWO_LAYER_HIERARCHY') {
    noInstitutionFoundStr = intl(
      'virtualvisitpatientportlet.region.no.institution.label'
    );
  }
  const noHospitalFoundStr = intl(
    'virtualvisitpatientportlet.region.no.hospital.label'
  );
  let selHospitalStr = intl('virtualvisitpatientportlet.region.hospital.label');
  let selInstitutionStr = intl(
    'virtualvisitpatientportlet.region.institutions.label'
  );
  const regionSelectedStr = intl(
    'virtualvisitpatientportlet.region.selected.label'
  );
  const closeSelectedRegionStr = intl(
    'virtualvisitpatientportlet.region.close.label'
  );
  const hospitalSelectedStr = intl(
    'virtualvisitpatientportlet.hospital.selected.label'
  );
  const closeSelectedHospitalStr = intl(
    'virtualvisitpatientportlet.hospital.close.label'
  );
  if (appointmentMode) {
    selHospitalStr = intl(
      'virtualvisitpatientportlet.region.hospital.appointment.label'
    );
    selInstitutionStr = intl(
      'virtualvisitpatientportlet.region.institutions.appointment.label'
    );
  } else if (reconnectSessionMode) {
    selHospitalStr = intl(
      'virtualvisitpatientportlet.region.hospitals.reconnectsession.label'
    );
    selInstitutionStr = intl(
      'virtualvisitpatientportlet.region.institutions.reconnectsession.label'
    );
  }

  const modeButtonsHidden = appointmentMode || reconnectSessionMode;
  const connectedByButtonsCls = loggedInPatient
    ? 'vvpp-single-button'
    : 'vvpp-multi-buttons';

  const [disableRejoining] = usePortletStore(store => [
    store.configuration.disableRejoining
  ]);

  function getElementId(el: Institution): string {
    return el.id;
  }

  const regionSelDivRef = useRef<HTMLDivElement | null>(null);
  const regionA11yProps = useListA11y({
    label: region ? regionSelectedStr : selRegionStr,
    elementList: regions,
    selectedElement: region,
    listContainerRef: regionSelDivRef,
    selectElement: selRegion,
    getElementId: getElementId
  });

  const institutionSelDivRef = useRef<HTMLDivElement | null>(null);
  const institutionA11yProps = useListA11y({
    label: selInstitutionStr,
    elementList: institutions,
    selectedElement: selectedInstitution ? selectedInstitution : null,
    listContainerRef: institutionSelDivRef,
    selectElement: selInst,
    getElementId: getElementId
  });

  const hospitalSelDivRef = useRef<HTMLDivElement | null>(null);
  const hospitalA11yProps = useListA11y({
    label: selHospitalStr,
    elementList: hospitals,
    selectedElement: hospital,
    listContainerRef: hospitalSelDivRef,
    selectElement: selHospital,
    getElementId: getElementId
  });

  return (
    <>
      <div className="vvpp-content" style={{ position: 'relative' }}>
        <h2>{intl('virtualvisitpatientportlet.region.description')}</h2>

        <h3>{region ? regionSelectedStr : selRegionStr}</h3>

        <ListBoxContainer
          className="vvpp-region-list-container"
          masked={institutionsLoading}
          ref={regionSelDivRef}
        >
          {!region &&
            regions.map(region => (
              <ListItem
                key={region.id}
                item={region}
                selectItem={selRegion}
                activeItem={regionA11yProps.activeElement}
              />
            ))}
          {region && (
            <Card
              key={region.id}
              item={region}
              onClose={selRegion}
              cardCloseLabel={closeSelectedRegionStr}
            />
          )}
        </ListBoxContainer>
        {!institutionsLoading && regions.length === 0 && (
          <h3 className="vvpp-no-institution-found-message">
            {intl('virtualvisitpatientportlet.no.region.found')}
          </h3>
        )}

        {showHospitals && (
          <div className="vvpp-hospitals">
            {hospitals.length > 0 && (
              <>
                <h3>{!hospital ? selHospitalStr : hospitalSelectedStr}</h3>
                <ListBoxContainer
                  className="vvpp-hospital-list-container"
                  masked={institutionsLoading}
                  ref={hospitalSelDivRef}
                >
                  {!hospital &&
                    hospitals.map(hosp => (
                      <ListItem
                        key={hosp.id}
                        item={hosp}
                        selectItem={selHospital}
                        activeItem={hospitalA11yProps.activeElement}
                      />
                    ))}
                  {hospital && (
                    <Card
                      key={hospital.id}
                      item={hospital}
                      onClose={setHospital}
                      cardCloseLabel={closeSelectedHospitalStr}
                    />
                  )}
                </ListBoxContainer>
              </>
            )}
            {!institutionsLoading && hospitals.length === 0 && (
              <h3 className="vvpp-no-institution-found-message">
                {noHospitalFoundStr}
              </h3>
            )}
          </div>
        )}

        {showInstitutions && (
          <div className="vvpp-institutions">
            {institutions.length > 0 && (
              <>
                <h3>{selInstitutionStr}</h3>
                <ListBoxContainer
                  className="vvpp-institution-list-container"
                  masked={institutionsLoading}
                  ref={institutionSelDivRef}
                >
                  {institutions.map(inst => (
                    <ListItem
                      key={inst.id}
                      item={inst}
                      selectItem={selInst}
                      activeItem={institutionA11yProps.activeElement}
                    />
                  ))}
                </ListBoxContainer>
              </>
            )}
            {!institutionsLoading && institutions.length === 0 && (
              <h3 className="vvpp-no-institution-found-message">
                {noInstitutionFoundStr}
              </h3>
            )}
          </div>
        )}

        {!modeButtonsHidden && !region && (
          <>
            <h3 className="vvpp-centered vvpp-connected-by">
              {disableRejoining
                ? intl(
                    'virtualvisitpatientportlet.region.institutions.connectedby.rejoin.disabled'
                  )
                : intl(
                    'virtualvisitpatientportlet.region.institutions.connectedby'
                  )}
            </h3>
            <div className={connectedByButtonsCls}>
              <Button
                id="vvpp-regionInstitution-appointment"
                variant="gray"
                text={intl(
                  'virtualvisitpatientportlet.region.button.appointment'
                )}
                onClick={() => {
                  setAppointmentMode(true);
                  setReconnectSessionMode(false);
                }}
              />
              {!loggedInPatient && !disableRejoining && (
                <Button
                  id="vvpp-regionInstitution-reconnect"
                  variant="gray"
                  text={intl(
                    'virtualvisitpatientportlet.region.button.reconnectSession'
                  )}
                  onClick={() => {
                    setReconnectSessionMode(true);
                    setAppointmentMode(false);
                  }}
                />
              )}
            </div>
          </>
        )}
      </div>
      {modeButtonsHidden && !region && (
        <div className="vvpp-single-button vvpp-buttons-separator">
          <Button
            id="vvpp-regionInstitution-back"
            variant="gray"
            text={intl('view.button.back')}
            onClick={() => {
              setReconnectSessionMode(false);
              setAppointmentMode(false);
            }}
          />
        </div>
      )}
    </>
  );
}

function ListItem(props: {
  item: Institution;
  selectItem: (newVal: Institution | null) => void;
  activeItem?: Institution | null;
}): JSX.Element {
  const onItemClick = () => {
    props.selectItem(props.item);
  };
  return (
    <div
      className={
        'vvpp-institution-list-item' +
        (props.item.name === props.activeItem?.name ? ' active' : '')
      }
      onClick={onItemClick}
      id={props.item.id}
      aria-label={props.item.name}
      role="option"
    >
      <div className="vvpp-institution-logo">
        {props.item.logo && (
          <img
            src={`data:image/jpeg;base64,${props.item.logo}`}
            alt="Institution logo"
          />
        )}
        {!props.item.logo && <div className="ehealthicon-hpd-institution" />}
      </div>
      <div className="vvpp-institution-text">
        <div>{props.item.name}</div>
      </div>
    </div>
  );
}

function Card(props: {
  item: Institution;
  onClose: (newVal: Institution | null) => void;
  cardCloseLabel: string;
}): JSX.Element {
  const onCardClose = () => {
    props.onClose(null);
  };
  return (
    <div
      role="listitem"
      aria-label={props.item.name}
      area-selected="true"
      className="vvpp-fixed-card vvpp-institution-list-item"
    >
      <div className="vvpp-institution-logo">
        {props.item.logo && (
          <img
            src={`data:image/jpeg;base64,${props.item.logo}`}
            alt="Institution logo"
          />
        )}
        {!props.item.logo && <div className="ehealthicon-hpd-institution" />}
      </div>
      <div className="vvpp-fixed-card-text">
        <div>{props.item.name}</div>
      </div>
      <Tool
        className="close-icon vvpp-fixed-card-close"
        variant="transparent"
        text=""
        aria-label={props.cardCloseLabel}
        icon="materialicon-close"
        onClick={onCardClose}
      />
    </div>
  );
}

// Panel put an unwished on an inner dev -> create own container with masking capability
const ListBoxContainer = forwardRef(
  (
    props: { children?: React.ReactNode; masked: boolean; className?: string },
    ref: Ref<HTMLDivElement>
  ) => {
    return (
      <div tabIndex={0} className={props.className} ref={ref} role="listbox">
        {props.children}
        <LoadMask masked={props.masked} />
      </div>
    );
  }
);
